import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	USER_CONTEXT_CACHE_TIMEOUT,
	USER_CONTEXT_PERSIST_TIME_KEY,
	USER_CONTEXT_URL,
	userContextDefaultState,
} from './const';
import { User, UserAPI } from './types';
import useFetch from '../../hooks/useFetch';
import { CacheManager } from '../../utils/cacheManager';
import { getUserContextFromDOM } from './utils';
import { setCookieValues } from '../app/components/CookieAccept/utils';
import { createProvider } from '../../utils/createProvider';

const [UserProvider, useUserPageContextState] = createProvider<User>({
	contextName: 'UserContext',
});

const [UserApiProvider, useUserPageContextAPI] = createProvider<UserAPI>({
	contextName: 'UserContextAPI',
});

const UserContextProvider = ({ children }) => {
	const cacheManagerRef = useRef<CacheManager>(
		new CacheManager(USER_CONTEXT_PERSIST_TIME_KEY, USER_CONTEXT_CACHE_TIMEOUT, {
			resetStartDateImmediately: true,
		}),
	);
	const { fetch, response, isPending } = useFetch<User>(USER_CONTEXT_URL);
	const fetchPendingRef = useRef<boolean>(isPending);
	const shouldFetch = useCallback((): boolean => {
		if (fetchPendingRef.current) {
			return false;
		}

		return cacheManagerRef.current.hasInvalidateCacheTime();
	}, []);
	// @ts-ignore
	const [userState, setUserState] = useState<User>(() => {
		return {
			...userContextDefaultState,
			...getUserContextFromDOM(),
			isInitialized: true,
		};
	});
	const tryFetch = useCallback(() => {
		if (shouldFetch()) {
			return fetch();
		}
	}, [shouldFetch, fetch]);

	useEffect(() => {
		fetchPendingRef.current = isPending;
	}, [isPending]);

	useEffect(() => {
		const responseData = response.data;

		if (typeof responseData === 'undefined') {
			return;
		}

		cacheManagerRef.current.resetStartDate();

		setUserState((prevValue) => ({
			...prevValue,
			...responseData,
			isInitialized: true,
			isPending: false,
		}));
	}, [response]);

	const userContextApiValue = useMemo<UserAPI>(
		() => ({
			fetchUserContext: fetch,
			tryFetchUserContext: tryFetch,
			resetUserContextCache: () => {
				cacheManagerRef.current.resetStartDate();
			},
			setUserAvatar: (photoUrl) => {
				const hasPhoto = photoUrl !== null && photoUrl.trim() !== '';

				setUserState((prevState) => ({
					...prevState,
					avatar: {
						hasPhoto,
						photoUrl,
					},
				}));
			},
			acceptCookie: () => {
				setUserState((prevState) => ({
					...prevState,
					hasAcceptedCookie: true,
				}));
				setCookieValues();
			},
		}),
		[fetch, tryFetch],
	);

	return (
		<UserProvider value={userState}>
			<UserApiProvider value={userContextApiValue}>{children}</UserApiProvider>
		</UserProvider>
	);
};

export { UserContextProvider, useUserPageContextState, useUserPageContextAPI };
